import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import { subsetGetter, SubsetGetterProps, Subsets, subsetSetter } from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import cloneDeep from "lodash/cloneDeep";

export type Trombosprofylax = {
    id: number;
    code: string;
    displayName: string;
    ordning: number;
};

export type TrombosprofylaxDisplay = {
    value: Trombosprofylax["id"];
    label: string;
};

type State = BaseState & {
    trombosprofylax: Map<Trombosprofylax["id"], Trombosprofylax>;
};

export const convertTrombosprofylaxData: (args: {
    trombosprofylax?: Trombosprofylax;
}) => TrombosprofylaxDisplay | null = ({ trombosprofylax }) => {
    if (!trombosprofylax) {
        return null;
    }

    return {
        value: trombosprofylax.id,
        label: trombosprofylax.displayName,
    };
};

/**
 * Varje default state ska innehålla BaseState + det som är specifikt för den här modulen.
 * Se helpers/Basestate
 */
const getDefaultState: () => State = () => {
    return {
        trombosprofylax: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "trombosprofylax";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        trombosprofylax: (state: State) => [...state.trombosprofylax.values()],
        trombosprofylaxSubset: (state: State) => (props: SubsetGetterProps<State> & { displayMode?: boolean }) => {
            const result = subsetGetter<State>({
                ...props,
                state,
                limit: 0,
                collectionName: moduleName,
            });

            if (props.displayMode && Array.isArray(result)) {
                return result.map((singleResult) => convertTrombosprofylaxData({ trombosprofylax: singleResult }));
            }

            return result;
        },
        trombosprofylaxById:
            (state: State) =>
            ({ id }: { id?: number }) => {
                if (!id) {
                    return null;
                }

                return state.trombosprofylax.get(id);
            },
    },
    /**
     * Mutations ska alltid innehålla baseMutations + det som är specifikt för modulen
     */
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addTrombosprofylax: (
            state: State,
            {
                offset,
                resetSubset,
                subsetName = "default",
                trombosprofylax = [],
            }: {
                resetSubset?: boolean;
                offset: number;
                subsetName: keyof Subsets;
                trombosprofylax: Trombosprofylax[];
            }
        ) =>
            subsetSetter<State, Trombosprofylax>({
                offset,
                subsetName,
                state,
                resetSubset,
                collectionName: moduleName,
                items: trombosprofylax,
            }),
    },
    actions: {
        ...baseActions({
            moduleName,
        }),
        fetchTrombosprofylax: async (
            { commit, state }: BaseActionsProps<State>,
            {
                displayMode,
                filterParams = {},
                config = {},
            }: {
                offset?: number;
                filterParams?: FilterParams;
                config?: {
                    tabbnamn?: string;
                };
                displayMode?: boolean;
            }
        ) => {
            try {
                const {
                    subsetName,
                    results,
                    total,
                    nextPage,
                    offset: resultOffset,
                } = await fetchItems<State, Trombosprofylax>({
                    filterParams,
                    state,
                    config,
                    page: 1,
                    limit: 0,
                    collectionName: moduleName,
                    apiUrl: "operation/kodverk/trombosprofylaxer/",
                });

                if (!results) {
                    return {
                        subsetName,
                        total,
                        nextPage,
                    };
                }

                commit("addTrombosprofylax", {
                    subsetName,
                    offset: resultOffset,
                    trombosprofylax: results,
                });

                return {
                    total,
                    nextPage,
                    subsetName,
                    results: displayMode
                        ? results.map((singleResult) => convertTrombosprofylaxData({ trombosprofylax: singleResult }))
                        : results,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
